import { useState, useEffect, useMemo } from 'react';
import { useBowtieSpin } from './useBowtieSpin';
import { useBowtiePosition } from './useBowtiePosition';
import interviewStyles from '../components/ResumeInterview.module.css';

interface BowtieControllerOptions {
  chatAreaRef: React.RefObject<HTMLDivElement | null>;
}

export function useBowtieController({ chatAreaRef }: BowtieControllerOptions) {
  // State machines
  const spin = useBowtieSpin();
  const position = useBowtiePosition();
  
  // Actual position state
  const [bowtiePosition, setBowtiePosition] = useState(10);
  
  // Helper function to calculate next empty position
  const calculateNextPosition = () => {
    if (!chatAreaRef.current) return 10;
    
    const messageElements = chatAreaRef.current.querySelectorAll(`.${interviewStyles.message}`);
    if (messageElements.length === 0) return 10;
    
    const lastMessage = messageElements[messageElements.length - 1] as HTMLElement;
    return lastMessage.offsetTop + lastMessage.offsetHeight + 20 + 9; // 20px margin + 5px offset
  };
  
  // Handle reactive positioning when state changes
  useEffect(() => {
    if (position.needsRepositioning && chatAreaRef.current) {
      // Calculate and set new position
      const newPosition = calculateNextPosition();
      setBowtiePosition(newPosition);
      
      // Mark repositioning as complete
      position.repositioningComplete();
    }
  }, [position, position.needsRepositioning, chatAreaRef]); // Include all dependencies
  
  // Combined API
  const api = useMemo(() => ({
    // User actions - start spinning AND trigger repositioning
    onUserAction: () => {
      spin.startSpinning();
      position.triggerRepositioning();
    },
    
    // Welcome message - stop spinning, NO repositioning
    onWelcomeMessage: () => {
      spin.stopSpinning();
    },
    
    // Auto text - stop spinning, NO repositioning
    onAutoText: () => {
      spin.stopSpinning();
    },
    
    // Stream start - stop spinning, NO repositioning
    onStreamStart: () => {
      spin.stopSpinning();
    },
    
    // Text started - stop spinning, NO repositioning
    onTextStarted: () => {
      spin.stopSpinning();
    },
    
    // Form received - trigger repositioning, keep spinning
    onFormReceived: () => {
      position.triggerRepositioning();
    },
    
    // Tool execution complete - trigger repositioning, keep spinning
    onToolExecutionComplete: () => {
      position.triggerRepositioning();
    },
    
    // Transition tool selected - trigger repositioning immediately
    onTransitionToolSelected: () => {
      position.triggerRepositioning();
    },
    
    // Response completed - stop spinning
    onResponseCompleted: () => {
      spin.stopSpinning();
    },
    
    // State accessors
    isSpinning: spin.isSpinning,
    bowtiePosition: bowtiePosition,
    
    // For debugging
    currentSpinState: spin.isSpinning ? 'SPINNING' : 'NOT_SPINNING',
    currentPositionState: position.needsRepositioning ? 'NEEDS_REPOSITIONING' : 'IDLE',
  }), [spin, position, bowtiePosition]);
  
  return api;
}